import { useLocalSearchParams } from 'expo-router'
import { autoLocationForJobAddress } from '@/hooks/jobs'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useJobForm } from '@/hooks/useJobForm'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { handleMutationErrorsWithForm } from '@/lib/errors'
import { DEFAULT_LOCATION_NAME, uploadLocationThumbnail } from '@/lib/locations'
import { performMutations, useMutation } from '@/lib/mutations'
import { newRecordId, useStore } from '@/lib/pocketbase'
import { isAddressBlank, type JobFormData } from '@/types/jobs'
import type { Addresses } from '@/types/pbSchema'
import { FormErrorSummary } from '@/ui/form'
import { JobFormFields } from '@/ui/jobs/JobFormFields'
import { Page } from '@/ui/Page'

export default function NewJobPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const { navigateBackTo } = useNavigateBack()
    const [addressesCollection, jobsCollection, locationsCollection] = useStore(
        'addresses',
        'jobs',
        'locations'
    )
    const { org: currentOrg } = useCurrentOrg()

    const {
        control,
        handleSubmit: formHandleSubmit,
        setError,
        getValues,
        setValue,
        formState: { errors, isSubmitted },
        isSubmitting,
        onSubmitRef,
    } = useJobForm()

    const createJob = useMutation({
        mutationFn: async (formData: JobFormData) => {
            const locationData = await autoLocationForJobAddress(
                formData.expand.address as Addresses
            )

            let newLocationId: string | null = null

            const result = await performMutations(function* () {
                let addressId = ''

                const hasAddress = !isAddressBlank(formData.expand.address)
                if (hasAddress) {
                    addressId = newRecordId()
                    yield addressesCollection.insert({
                        ...formData.expand.address,
                        id: addressId,
                        org: currentOrg.id,
                    })
                }

                const jobId = newRecordId()
                yield jobsCollection.insert({
                    id: jobId,
                    identifier: formData.identifier,
                    name: formData.name,
                    notes: formData.notes || '',
                    status: formData.status,
                    started: formData.started || '',
                    completed: formData.completed || '',
                    org: currentOrg.id,
                    address: addressId,
                    customer: formData.customer?.id || '',
                    tags: formData.tags ?? [],
                })

                if (locationData) {
                    newLocationId = newRecordId()
                    yield locationsCollection.insert({
                        id: newLocationId,
                        name: DEFAULT_LOCATION_NAME,
                        org: currentOrg.id,
                        job: jobId,
                        inactive: false,
                        ...locationData,
                    })
                }
            })

            if (newLocationId && locationData) {
                uploadLocationThumbnail({
                    id: newLocationId,
                    name: formData.name,
                    geometry: locationData.geometry,
                })
            }

            return result
        },
        onSuccess: () => {
            navigateBackTo(`/${params.orgSlug}/settings/jobs/`)
        },
        onError: handleMutationErrorsWithForm({ setError, getValues }),
    })

    onSubmitRef.current = (data: JobFormData) => createJob.mutate(data)

    useHeaderOptions({
        buttons: {
            type: 'create-save',
            onPress: formHandleSubmit,
            loading: isSubmitting || createJob.isPending,
        },
    })

    return (
        <Page>
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            <JobFormFields
                control={control as unknown as import('react-hook-form').Control<JobFormData>}
                setValue={setValue}
            />
        </Page>
    )
}
